import type { JSX } from 'react';
import { useContext, useEffect } from 'react';
import { Modal as VHSModal } from '@wistia/vhs';
import { useSnowplow } from '~/onboarding/hooks/useSnowplow/useSnowplow';
import type { Trackable } from '~/onboarding/types';
import type { IconNameType } from '@wistia/vhs';
import { useLogger } from '../../hooks/useLogger';
import { withErrorBoundary, useErrorBoundary } from '../../hooks/useErrorBoundary';
import { OnboardingContext } from '../../hooks/useOnboardingContext/OnboardingContext';
import type { HeroType } from './shared/Hero';
import { HeroModalContent } from './shared/HeroModalContent';
import { ModalCloseButton } from './shared/ModalCloseButton';
import type { ModalFooterProps } from './shared/ModalFooter';
import { ModalFooter } from './shared/ModalFooter';
import { DEFAULT_HERO_HEIGHT } from './shared/constants';
import type { SharedModalProps } from './types';

export type ModalProps =
  // Require CTA props
  Partial<Pick<ModalFooterProps, 'cancelLabel' | 'onClickCancel'>> &
    Pick<ModalFooterProps, 'ctaLabel' | 'onClickCta'> &
    Pick<SharedModalProps, 'isOpen' | 'onRequestClose'> &
    Trackable & {
      /**
       * Text for the header
       */
      header: string;
      /**
       * Text for the body
       */
      content: string;
      /**
       * `<Hero>` component to render an image, gif, or video
       */
      hero: React.ReactElement<HeroType>;
      /**
       * Allows user to specify height (in pixels) of hero
       */
      height?: number;
      /**
       * Specify the type of media that you are rendering
       */
      mediaType: 'image' | 'video';
      /**
       * Name of the icon to display
       */
      iconName?: IconNameType;
      /**
       * Position of the icon
       */
      iconPosition?: 'end' | 'start';
      /**
       * Variant of the header
       */
      headerVariant?: 'headline1' | 'headline2' | 'headline3';
    };

export const ModalComponent = ({
  isOpen,
  onRequestClose,
  hero,
  header,
  content,
  ctaLabel,
  onClickCta,
  cancelLabel,
  onClickCancel,
  mediaType,
  iconName,
  iconPosition,
  headerVariant = 'headline1',
  height = DEFAULT_HERO_HEIGHT,
  snowplowParams,
}: ModalProps): JSX.Element => {
  const context = useContext(OnboardingContext);
  const logger = useLogger(context?.getCurrentFeatureId());
  useErrorBoundary((error: Error) => logger.error(error));

  const { logEvent } = useSnowplow();

  useEffect(() => {
    if (isOpen) {
      logEvent('modalView', snowplowParams);
    }
  }, [isOpen, logEvent, snowplowParams]);

  const handleCancel = onClickCancel
    ? () => {
        logEvent('modalDismiss', snowplowParams);
        onClickCancel();
      }
    : undefined;

  return (
    <VHSModal
      isOpen={isOpen}
      onRequestClose={() => {
        logEvent('modalDismiss', snowplowParams);
        onRequestClose();
      }}
    >
      <ModalCloseButton />
      <HeroModalContent
        headerVariant={headerVariant}
        content={content}
        header={header}
        height={height}
        hero={hero}
        mediaType={mediaType}
      />
      <ModalFooter
        ctaLabel={ctaLabel}
        onClickCta={() => {
          logEvent('modalClick', snowplowParams);
          onClickCta();
        }}
        cancelLabel={cancelLabel}
        onClickCancel={handleCancel}
        iconName={iconName}
        iconPosition={iconPosition}
      />
    </VHSModal>
  );
};

export const Modal = withErrorBoundary(ModalComponent);
